/**
 * @fileoverview Sentry error tracking and logging utilities.
 */

import * as Sentry from "@sentry/react";
import {BrowserTracing} from "@sentry/tracing";
import {reactAppEnvironment} from "../config";
import {history} from "./history";

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    replaysSessionSampleRate: 0.001,
    replaysOnErrorSampleRate: 1,
    integrations: [
      new BrowserTracing({
        routingInstrumentation: Sentry.reactRouterV5Instrumentation(history),
      }),
    ],
    tracesSampleRate: 0.01,
    environment: reactAppEnvironment,
  });
}

/**
 * Errors that should not be logged to Sentry, identified by message.
 */
const IGNORED_ERRORS = new Set(["Failed to fetch", "Request aborted"]);

export const logErrorToSentry = import.meta.env.VITE_SENTRY_DSN
  ? (error: unknown, info: unknown = {}) => {
      if (
        typeof error === "object" &&
        error !== null &&
        "message" in error &&
        IGNORED_ERRORS.has((error as Error).message)
      ) {
        return; // Do not log ignored errors
      }

      Sentry.withScope(scope => {
        Object.keys(info).forEach(key => {
          // @ts-expect-error info error
          scope.setExtra(key, info[key]);
        });

        // Better error serialization
        let errorString;
        if (error instanceof Response) {
          errorString = `Response error: ${error.status} ${error.statusText} - ${error.url}`;
        } else if (typeof error === "object" && error !== null) {
          try {
            errorString = JSON.stringify(error);
          } catch {
            errorString = `[object ${error.constructor?.name || "Object"}]`;
          }
        } else {
          errorString = String(error);
        }

        scope.setExtra("error", errorString);

        // Check if this is an HTTP client error (like Axios error or apisauce response)
        if (
          typeof error === "object" &&
          error !== null &&
          "config" in error &&
          "data" in error
        ) {
          // Handle HTTP error - create a proper Error object with meaningful message
          // apisauce has `status` directly on response, Axios errors have it nested under `.response`
          const httpError = error as any;
          const status = httpError.status ?? httpError.response?.status;
          const message = `Request failed: ${httpError.config?.method?.toUpperCase()} ${
            httpError.config?.url
          } - ${status ?? "Unknown status"}`;
          Sentry.captureException(new Error(message));
        } else if (error instanceof Response) {
          // Handle Response objects specifically
          const message = `HTTP ${error.status}: ${error.statusText} - ${error.url}`;
          Sentry.captureException(new Error(message));
        } else {
          // Handle other errors - capture directly
          Sentry.captureException(error);
        }
      });
    }
  : (error: unknown, info: unknown = {}) => {
      // eslint-disable-next-line no-console
      console.error(
        "This error would be logged to sentry:",
        error,
        "Info would be logged to sentry:",
        info,
      );
    };
